// src/components/PremiumFeatures.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import featureAccess from '../utils/featureAccess';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import { USER_TIERS } from '../services/UserService';

/**
 * Component that displays available premium features and upgrade options
 */
function PremiumFeatures() {
  const { userTier } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  
  // Get features based on tier
  const tierFeatures = featureAccess.getTierFeatures();
  
  // Get pro feature details for marketing
  const proFeatureDetails = [
    featureAccess.getProFeatureDetails('savedWorkouts'),
    featureAccess.getProFeatureDetails('userProfiles'),
    featureAccess.getProFeatureDetails('detailedAnalytics'),
    featureAccess.getProFeatureDetails('workoutPrograms'),
    featureAccess.getProFeatureDetails('exportPDF'),
    featureAccess.getProFeatureDetails('customModifications')
  ];
  
  // Check if user is on pro tier
  const isPro = userTier === USER_TIERS.PRO;
  
  // Handle subscription update success
  const handleSubscriptionSuccess = () => {
    setIsUpgradeModalOpen(false);
    // You could add additional logic here, like showing a success message
    // or refreshing the page to reflect the user's new subscription status
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isPro ? 'Your Pro Features' : 'Upgrade to Pro'}
        </h2>
        <p className="text-gray-600 max-w-md mx-auto">
          {isPro 
            ? 'You have access to all premium features. Enjoy your enhanced Pilates experience!' 
            : 'Unlock all premium features and take your Pilates workouts to the next level.'}
        </p>
      </div>
      
      {/* Feature comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Free tier */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Guest</h3>
            <p className="text-gray-500 text-sm">Basic access</p>
            <p className="text-xl font-bold mt-2">Free</p>
          </div>
          
          <ul className="space-y-2">
            {tierFeatures[USER_TIERS.NONE].map((feature, index) => (
              <li key={index} className="flex items-start text-sm">
                <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Basic tier */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Basic</h3>
            <p className="text-gray-500 text-sm">Standard access</p>
            <p className="text-xl font-bold mt-2">Free with Registration</p>
          </div>
          
          <ul className="space-y-2">
            {tierFeatures[USER_TIERS.BASIC].map((feature, index) => (
              <li key={index} className="flex items-start text-sm">
                <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Pro tier */}
        <div className="border-2 border-blue-500 rounded-lg p-4 relative">
          <div className="absolute -top-3 right-4 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
            RECOMMENDED
          </div>
          
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Premium</h3>
            <p className="text-gray-500 text-sm">All features unlocked</p>
            <p className="text-xl font-bold mt-2">$1.99/month</p>
            <p className="text-sm text-gray-500">or $19.99/year (save 16%)</p>
          </div>
          
          <ul className="space-y-2">
            {tierFeatures[USER_TIERS.PRO].map((feature, index) => (
              <li key={index} className="flex items-start text-sm">
                <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className={index < 3 ? "text-gray-600" : "text-gray-800 font-medium"}>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Premium Features Showcase */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-center mb-6">Premium Features</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {proFeatureDetails.map((feature, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="mb-3 text-3xl">{feature.icon}</div>
              <h4 className="text-lg font-medium text-gray-800 mb-1">{feature.name}</h4>
              <p className="text-sm text-gray-600">{feature.description}</p>
              {feature.basicLimit && (
                <div className="mt-2 text-xs text-gray-500">
                  Basic limit: {feature.basicLimit}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Call to action */}
      {!isPro && (
        <div className="text-center">
          <button 
            onClick={() => setIsUpgradeModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors"
          >
            Upgrade Now
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Try risk-free with our 7-day money-back guarantee
          </p>
        </div>
      )}
      
      {/* Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
}

export default PremiumFeatures;
